/**
 * Base Freesound Asset
 */

import { BaseAsset } from '../../base';
import { MediaType, ExternalAssetSource } from '../../../types';
import type { IAssetMetadata } from '../../base/types';
import type { FreesoundSound } from '../types';
import { getBlobDataFromUrl } from '../../../utils';

export class BaseFreesoundAsset extends BaseAsset {
  protected sound: FreesoundSound;
  protected apiKey: string;
  protected baseUrl: string;

  constructor(sound: FreesoundSound, apiKey: string, baseUrl: string) {
    super();
    this.sound = sound;
    this.apiKey = apiKey;
    this.baseUrl = baseUrl;
  }

  getId(): string {
    return String(this.sound.id);
  }

  getName(): string {
    return this.sound.name;
  }

  setName(): void {
    // Freesound is read-only, so renaming is not supported
    throw new Error('Cannot rename assets in Freesound - it is a read-only provider');
  }

  getMediaType(): MediaType {
    return MediaType.Audio;
  }

  async getImageUrlForCard(_options?: { width?: number; height?: number }): Promise<string> {
    // Return waveform image as thumbnail
    // Use medium size for thumbnails
    // TODO: Could use _options to return waveform_l for larger sizes
    return this.sound.images.waveform_m;
  }

  async getPreviewUrl(size?: { width: number; height?: number }) {
    return this.sound.previews['preview-hq-mp3'];
  }

  async getDownloadUrl(): Promise<string> {
    // Freesound download URLs require OAuth2, so we return the preview URL instead
    // For actual download, users would need to implement OAuth2 flow
    return this.sound.previews['preview-hq-mp3'];
  }

  async downloadFile(): Promise<{ blob: Blob, filename: string }> {
    // This should be implemented to fetch the actual file
    // For now, return a previews file
    const { blob } = await getBlobDataFromUrl(this.sound.previews['preview-hq-mp3'])
    return { blob, filename: `${this.sound.name}.mp3` };
  }

  downloadFileVersion(versionId: string): Promise<{ blob: Blob, filename: string }> {
    // Freesound doesn't support versions, so this is a no-op
    return Promise.reject(new Error('Versioning not supported'));
  }

  getSource(): string {
    return ExternalAssetSource.Freesound;
  }

  getMetadata(): IAssetMetadata {
    return {
      status: 'ready',
      date: this.sound.created || '',
      size: this.sound.filesize || 0,
      // Additional Freesound-specific metadata
      id: this.sound.id,
      name: this.sound.name,
      tags: this.sound.tags,
      description: this.sound.description,
      username: this.sound.username,
      licence: this.sound.license || 'Freesound-licence',
      licenceUrl: 'https://freesound.org/help/faq/#licenses',
      duration: this.sound.duration,
      type: this.sound.type,
      channels: this.sound.channels,
      bitrate: this.sound.bitrate,
      samplerate: this.sound.samplerate,
      downloads: this.sound.num_downloads,
      rating: this.sound.avg_rating,
      ratings: this.sound.num_ratings,
      source: 'Freesound',
      public: true,
      categories: this.sound.tags, // Using tags as categories for filtering purposes
    };
  }
}
